import { useEffect, useCallback } from 'react'
import { getVoiceSettings } from '../lib/storage'

interface UseTextToSpeechOptions {
  language: string
  enabled?: boolean
}

/**
 * Hook for text-to-speech functionality.
 * Sets up window.speakText global function and handles voice loading/cleanup.
 */
export function useTextToSpeech({ language, enabled = true }: UseTextToSpeechOptions) {
  const speakText = useCallback((text: string) => {
    if (!enabled || !('speechSynthesis' in window)) return

    const utterance = new SpeechSynthesisUtterance(text)

    // Get available voices
    const voices = window.speechSynthesis.getVoices()

    // Check for saved voice preference
    const voiceSettings = getVoiceSettings()
    const preferredVoice = voiceSettings[language]
    let selectedVoice: SpeechSynthesisVoice | null = null

    if (preferredVoice) {
      if (preferredVoice.includes('|')) {
        // New format with index
        const [, indexStr] = preferredVoice.split('|')
        const index = parseInt(indexStr, 10)
        selectedVoice = voices[index] || null
      } else {
        // Old format, try to find by URI
        selectedVoice = voices.find(voice => voice.voiceURI === preferredVoice) || null
      }
    }

    // If no saved preference or voice not found, use the default for the language
    if (!selectedVoice) {
      selectedVoice = voices.find(voice =>
        voice.lang.toLowerCase().startsWith(language.toLowerCase())
      ) || null
    }

    // Set the voice if we found one
    if (selectedVoice) {
      utterance.voice = selectedVoice
    }

    // Set other properties for better quality
    utterance.rate = 0.9 // Slightly slower for clarity
    utterance.pitch = 1.0
    utterance.volume = 1.0

    // Cancel any ongoing speech
    window.speechSynthesis.cancel()

    // Speak
    window.speechSynthesis.speak(utterance)
  }, [language, enabled])

  // Set up voice loading and global speakText function
  useEffect(() => {
    if (!('speechSynthesis' in window)) return

    // Load voices when they become available
    const loadVoices = () => {
      window.speechSynthesis.getVoices()
    }

    // Some browsers need this event to load voices
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices)
    loadVoices() // Try loading immediately too

    // Set up global function for TTS buttons in CardContent
    ;(window as any).speakText = speakText

    return () => {
      delete (window as any).speakText
      window.speechSynthesis.cancel()
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices)
    }
  }, [speakText])

  return { speakText }
}
